import { formatTokenCount } from './format';
import { getMinPricing, getTotalRegionCount, type UnifiedModel } from './unifiedModels';

export interface ModelResidencySummary {
  label: string;
  className: string;
}

export function getModelResidencySummary(model: UnifiedModel): ModelResidencySummary {
  const scopes = new Set(model.deployments.map((deployment) => deployment.residency));

  if (scopes.size === 0) return { label: 'Unknown', className: '' };
  if (scopes.has('global') || scopes.has('cross-region')) {
    return scopes.size === 1
      ? { label: 'Global', className: 'console-chip-amber' }
      : { label: 'Mixed routing', className: 'console-chip-amber' };
  }
  if (scopes.has('geo-boundary')) {
    return scopes.size === 1
      ? { label: 'Geo boundary', className: 'console-chip-blue' }
      : { label: 'Mixed regional', className: 'console-chip-blue' };
  }
  if (scopes.has('single-region')) {
    return { label: 'Strict region', className: 'console-chip-emerald' };
  }

  return { label: 'Unknown', className: '' };
}

export function getModelCapabilityLabels(model: UnifiedModel, maxLabels = 4): string[] {
  const labels: string[] = [];
  const normalizedText = [
    model.slug,
    model.displayName,
    model.bestFor,
    model.shortDescription,
    model.description,
    model.useCases?.join(' '),
    model.strengths?.join(' '),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const modalities = new Set([
    ...model.capabilities.inputModalities,
    ...model.capabilities.outputModalities,
  ]);

  if (/\b(reasoning|thinking|agentic)\b/.test(normalizedText)) labels.push('Reasoning');
  if (/\b(code|coding|coder|programming|software)\b/.test(normalizedText) || modalities.has('CODE')) {
    labels.push('Code');
  }
  if (/\b(tool use|function calling|function-calling)\b/.test(normalizedText)) labels.push('Tool use');
  if (modalities.has('IMAGE')) labels.push('Vision');
  if (modalities.has('AUDIO')) labels.push('Audio');
  if (modalities.has('VIDEO')) labels.push('Video');
  if (model.capabilities.streaming) labels.push('Streaming');
  if (model.capabilities.contextWindow >= 1_000_000) {
    labels.push(`${formatTokenCount(model.capabilities.contextWindow)} context`);
  } else if (model.capabilities.contextWindow >= 100_000) {
    labels.push(`${formatTokenCount(model.capabilities.contextWindow)} context`);
  }

  if (labels.length === 0) {
    labels.push(...Array.from(modalities).slice(0, maxLabels).map((modality) => modality.toLowerCase()));
  }

  return Array.from(new Set(labels)).slice(0, maxLabels);
}

export function getModelBestForText(model: UnifiedModel): string {
  return (
    model.bestFor ||
    model.shortDescription ||
    model.useCases?.slice(0, 2).join(', ') ||
    model.strengths?.[0] ||
    'Review deployment coverage, regions, residency, latency, and provider options.'
  );
}

export function getModelRegionLabel(model: UnifiedModel): string {
  const regionCount = getTotalRegionCount(model);
  if (regionCount > 0) return `${regionCount}`;

  const hasGlobalRouting = model.deployments.some(
    (deployment) => deployment.residency === 'global' || deployment.deploymentType === 'global-endpoint'
  );
  return hasGlobalRouting ? 'Global' : 'Pending';
}

export function getModelPricingLabel(model: UnifiedModel): string {
  const pricing = getMinPricing(model);
  if (!pricing) return 'Pricing pending';

  return `$${pricing.input.toFixed(4)} in / $${pricing.output.toFixed(4)} out`;
}

export function getModelUpdatedTime(model: UnifiedModel): number {
  return Math.max(
    Date.parse(model.releaseDate ?? '') || 0,
    Date.parse(model.firstCreated) || 0,
    Date.parse(model.lastUpdated) || 0
  );
}
